/*
FindoutAuth
findoutのみでの認証方式
*/

import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';

// third-party
import { useCookies } from 'react-cookie';
import jwt from 'jsonwebtoken';

// project imports
import accountReducer from 'store/accountReducer';
import { LOGOUT, LOGIN } from 'store/actions';
import Loader from 'ui-component/Loader';
import { verifyFoIdtoken, revokeIdtoken } from 'utils/function/auth';

const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  redirectFailed: false,
  user: null,
  userProfile: '',
  url: '',
  foIdtoken: ''
};

// リファラ情報を取得
const referrer = window.document.referrer;

// チャンネルID,遷移前のURL取得
const url = new URL(window.location.href);

// liff用のstateパラメータ置換
let search = decodeURIComponent(url.search).replace('?liff.state=', '');
search = decodeURIComponent(search).replace('#state', '');

const query = new URLSearchParams(search);

const foIdtoken = query.get('foidtk');

const FindoutContext = createContext(null);

// JWTトークンを検証・デコードする関数
const verifyAndDecodeToken = (token) => {
  try {
    // JWTの検証とデコード
    const decoded = jwt.verify(token, process.env.REACT_APP_JWT_SECRET);
    return { isValid: true, decoded };
  } catch (error) {
    console.error('JWT verification failed:', error);
    return { isValid: false, decoded: null };
  }
};

export const FindoutProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const navigate = useNavigate();
  const [cookies, setCookie, removeCookie] = useCookies();

  useEffect(() => {
    const verifyTokenAndUpdateState = async () => {
      try {
        if (foIdtoken || cookies.foidtk) {
          const token = foIdtoken ?? cookies.foidtk;

          // JWTトークンの検証とデコード
          const { isValid, decoded } = verifyAndDecodeToken(token);

          if (!isValid) {
            console.error('Invalid JWT token');
            dispatch({
              type: LOGOUT,
              payload: { url }
            });
            removeCookie('foidtk', { path: '/' });
            window.location.replace(`${process.env.REACT_APP_LOGOUT_CALLBACK}?status=401&referer=${window.location.origin}`);
            return;
          }

          const headers = {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            Authorization: `Bearer ${token}`
          };

          // Idtoken verify
          await verifyFoIdtoken(headers).then((res) => {
            if (res?.data?.uid) {
              // JWTの有効期限を取得してクッキーの有効期限を設定
              // expフィールドは Unix timestamp (秒単位) で格納されている
              // 例: "exp": 1786928348
              const currentTime = Date.now() / 1000; // 現在時刻を秒単位で取得
              const expiresIn = decoded.exp - currentTime; // 残り有効期限（秒）

              setCookie('foidtk', token, {
                path: '/',
                maxAge: Math.max(expiresIn, 60), // 最低1分は有効にする
                secure: true,
                sameSite: 'strict'
              });

              dispatch({
                type: LOGIN,
                payload: {
                  userProfile: res.data,
                  url,
                  tokens: {
                    foIdtoken: token,
                    jwt: process.env.REACT_APP_JWT_TOKEN
                  }
                }
              });
            } else if (!state.isLoggedIn) {
              dispatch({
                type: LOGOUT,
                payload: {
                  url
                }
              });
              removeCookie('foidtk', { path: '/' });
            }
          });
        } else {
          dispatch({
            type: LOGOUT,
            payload: {
              url
            }
          });
          removeCookie('foidtk', { path: '/' });
          window.location.replace(`${process.env.REACT_APP_LOGOUT_CALLBACK}?status=401&referer=${window.location.origin}`);
        }
      } catch (error) {
        // エラーハンドリング
        console.error('Token verification error:', error);
      }
    };
    verifyTokenAndUpdateState();
  }, [dispatch]);

  const logout = async () => {
    const headers = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      Authorization: `Bearer ${foIdtoken ?? cookies.foidtk}`
    };

    try {
      await revokeIdtoken(headers, {});
    } catch (error) {
      console.log('revokeIdtoken failed:', error);
    } finally {
      removeCookie('foidtk', { path: '/' });
      window.location.replace(`${process.env.REACT_APP_LOGOUT_CALLBACK}?status=401&referer=${window.location.origin}`);
    }
  };

  const handleSessionTimeout = async () => {
    await logout();
  };

  const checkTokenExpiration = async () => {
    if (!cookies.foidtk && !foIdtoken) {
      console.error('Invalid or missing token.');
      await handleSessionTimeout();
      return;
    }

    const token = foIdtoken ?? cookies.foidtk;

    // JWTトークンの検証とデコード
    const { isValid, decoded } = verifyAndDecodeToken(token);

    if (!isValid) {
      await handleSessionTimeout();
      return;
    }

    // expフィールドは Unix timestamp (秒単位) で格納されている
    // 例: "exp": 1786928348
    const currentTime = Date.now() / 1000; // 現在時刻を秒単位で取得
    const expiresIn = decoded.exp - currentTime; // 残り有効期限（秒）

    // 有効期限切れの場合のみログアウト
    if (expiresIn <= 0) {
      handleSessionTimeout();
    }
  };

  const repeatCheckTokenExpiration = async () => {
    await checkTokenExpiration();

    // 30分後に再度繰り返す
    setTimeout(repeatCheckTokenExpiration, 1800000);
  };

  // トークン有効期限確認用のタイマー関数処理
  useEffect(() => {
    repeatCheckTokenExpiration();
  }, []);

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <FindoutContext.Provider
      value={{
        ...state,
        url,
        dispatch,
        login: () => {},
        logout
      }}
    >
      {children}
    </FindoutContext.Provider>
  );
};

FindoutProvider.propTypes = {
  children: PropTypes.node
};

export default FindoutContext;
